import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Reveal from "@/components/base/Reveal";
import { articles } from "@/mocks/journal";

export default function JournalDetailPage() {
  const { slug } = useParams();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <SiteLayout>
        <div className="flex min-h-[70vh] flex-col items-center justify-center px-5 text-center">
          <p className="eyebrow">Story not found</p>
          <h1 className="mt-5 font-heading text-3xl text-foreground-950 md:text-5xl">
            That story isn't in the journal.
          </h1>
          <Link to="/journal" className="btn-primary mt-9">
            Back to the Journal
            <i className="ri-arrow-left-line text-base" />
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = articles.filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <SiteLayout>
      <article>
        <header className="relative flex min-h-[80vh] items-end overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} — Chef's Table`}
              className="h-full w-full object-cover object-center animate-kenburns"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/70 to-foreground-950/30" />

          <div className="relative z-10 w-full px-5 md:px-10 lg:px-16 pb-14 md:pb-20">
            <div className="max-w-3xl">
              <Reveal>
                <p className="eyebrow text-primary-400">{article.category}</p>
              </Reveal>
              <Reveal delay={120}>
                <h1 className="mt-5 font-heading text-4xl leading-[1.03] text-background-50 sm:text-5xl md:text-6xl lg:text-7xl">
                  {article.title}
                </h1>
              </Reveal>
              <Reveal delay={220}>
                <div className="mt-6 flex flex-wrap items-center gap-3 font-label text-[10px] uppercase tracking-label text-background-50/60">
                  <span>{article.author}</span>
                  <span className="h-1 w-1 rounded-full bg-background-50/40" />
                  <span>{article.date}</span>
                  <span className="h-1 w-1 rounded-full bg-background-50/40" />
                  <span>{article.readTime} read</span>
                </div>
              </Reveal>
            </div>
          </div>
        </header>

        <section className="bg-background-50">
          <div className="w-full px-5 md:px-10 lg:px-16 py-20 md:py-28">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
              <aside className="lg:col-span-3">
                <div className="lg:sticky lg:top-28">
                  <Link
                    to="/journal"
                    className="link-underline inline-flex items-center gap-2 font-label text-[11px] uppercase tracking-label text-primary-700"
                  >
                    <i className="ri-arrow-left-line text-base" />
                    The Chef's Table
                  </Link>
                  <div className="mt-8 hidden border-t border-foreground-950/12 pt-6 lg:block">
                    <p className="font-label text-[10px] uppercase tracking-label text-foreground-500">
                      Filed under
                    </p>
                    <p className="mt-2 font-body text-sm text-foreground-800">
                      {article.category}
                    </p>
                  </div>
                </div>
              </aside>

              <div className="lg:col-span-7 lg:col-start-5">
                <Reveal>
                  <p className="font-heading text-2xl leading-snug text-foreground-900 md:text-3xl">
                    {article.excerpt}
                  </p>
                </Reveal>

                <div className="mt-10 space-y-6">
                  {article.body.map((para, i) => (
                    <Reveal key={i} delay={i * 60}>
                      <p className="font-body text-base leading-relaxed text-foreground-700 md:text-lg">
                        {para}
                      </p>
                    </Reveal>
                  ))}
                </div>

                {article.body[1] && (
                  <Reveal>
                    <blockquote className="mt-12 border-l-2 border-primary-500 pl-6">
                      <p className="font-heading text-xl italic leading-snug text-foreground-900 md:text-2xl">
                        {article.body[1].split(". ")[0]}.
                      </p>
                    </blockquote>
                  </Reveal>
                )}
              </div>
            </div>
          </div>
        </section>
      </article>

      <section className="bg-background-100">
        <div className="w-full px-5 md:px-10 lg:px-16 py-20 md:py-24">
          <Reveal>
            <p className="eyebrow">More from the Table</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-3">
            {related.map((a, i) => (
              <Reveal key={a.slug} delay={i * 100}>
                <Link to={`/journal/${a.slug}`} className="group block">
                  <div className="aspect-[4/3] w-full overflow-hidden bg-foreground-900 image-zoom">
                    <img
                      src={a.image}
                      alt={a.title}
                      className="h-full w-full object-cover object-center"
                    />
                  </div>
                  <span className="mt-5 block font-label text-[10px] uppercase tracking-label text-primary-600">
                    {a.category}
                  </span>
                  <h3 className="mt-3 font-heading text-xl leading-snug text-foreground-950 transition-colors duration-300 group-hover:text-primary-600 md:text-2xl">
                    {a.title}
                  </h3>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}